// src/app/app.component.ts
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ToastComponent } from './toast/toast.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { AlertController, Platform, IonRouterOutlet } from '@ionic/angular';
import { App } from '@capacitor/app';
import { UpdateCheckService } from './services/update-check.service';
import { UpdateDialogComponent } from "./update-dialog/update-dialog.component";
import { TransmissionService } from './services/transmission.service';
import { ThemeConfigService } from './services/theme-config.service';
import { AnalyticsService } from './services/analytics.service';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastComponent, SpinnerComponent, UpdateDialogComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet!: IonRouterOutlet;
  showUpdateDialog = false;
  updateData: any = {};
  config: any;
  private backButtonPressedOnce = false;
  private backButtonTimeout: any;

  constructor(
    private platform: Platform,
    private alertCtrl: AlertController,
    private updateCheck: UpdateCheckService,
    private themeConfig: ThemeConfigService,
    private router: Router,
    private analytics: AnalyticsService,
    private seoService: SeoService
  ) {
    console.log("App component loading");
    
    // Deep link handler
    App.addListener('appUrlOpen', (data) => {
      const url = data.url;
      const path = url.replace('https://www.pdacademy.co.in', '');

      if (path.startsWith('/result/view/')) {
        const userExamId = path.split('/result/view/')[1];
        this.router.navigate(['/result/view', userExamId]);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Initialize Analytics
    this.analytics.initAutoTracking();

    // Initialize SEO
    this.seoService.initSEO();

    // Add Organization and Website schemas
    this.seoService.createStructuredData({
      '@context': 'https://schema.org',
      '@graph': [
        this.seoService.getOrganizationSchema(),
        this.seoService.getWebsiteSchema()
      ]
    });

    // Load theme configuration
    this.themeConfig.loadConfig().subscribe((config) => {
      this.themeConfig.setTheme(config);
    });

    await this.platform.ready();

    this.initializeApp();

    // Check for app update (with error handling)
    await this.checkAppUpdate();
  }

  /**
   * Check for app updates
   */
  private async checkAppUpdate(): Promise<void> {
    try {
      const update = await this.updateCheck.checkForAppUpdate();
      
      if (update) {
        console.log('Update available:', update);
        this.updateData = update;
        this.showUpdateDialog = true;
      } else {
        console.log('App is up to date');
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
      // Don't show error to user - gracefully fail
    }
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.platform.backButton.subscribeWithPriority(-1, async () => {
        // Check if we can go back in the navigation stack
        if (!this.routerOutlet || !this.routerOutlet.canGoBack()) {
          // If on root route, implement double-tap to exit
          if (this.isOnRootRoute()) {
            await this.handleDoubleBackToExit();
          } else {
            // Navigate to root/dashboard instead of exiting
            this.router.navigate(['/dashboard']);
          }
        } else {
          // Normal back navigation
          this.routerOutlet.pop();
        }
      });
    });
  }

  /**
   * Check if user is on root/main route (dashboard)
   */
  private isOnRootRoute(): boolean {
    const currentUrl = this.router.url;
    const rootRoutes = ['/', '/dashboard', '/home'];
    return rootRoutes.some(route => 
      currentUrl === route || currentUrl.startsWith(route + '?')
    );
  }

  /**
   * Handle double back press to exit
   */
  private async handleDoubleBackToExit() {
    if (this.backButtonPressedOnce) {
      // Second press within timeout - exit app
      clearTimeout(this.backButtonTimeout);
      App.exitApp();
    } else {
      // First press - show toast and set timer
      this.backButtonPressedOnce = true;
      
      // Show toast notification
      await this.showExitToast();
      
      // Reset after 2 seconds
      this.backButtonTimeout = setTimeout(() => {
        this.backButtonPressedOnce = false;
      }, 2000);
    }
  }

  /**
   * Show toast for "Press back again to exit"
   */
  private async showExitToast() {
    const toast = document.createElement('ion-toast');
    toast.message = 'Press back again to exit';
    toast.duration = 2000;
    toast.position = 'bottom';
    toast.cssClass = 'exit-toast';
    
    document.body.appendChild(toast);
    await toast.present();
  }

  /**
   * Handle update dialog cancel
   */
  onCancel() {
    if (!this.updateData.mandatory) {
      this.showUpdateDialog = false;
    }
  }

  /**
   * Handle update dialog confirm
   */
  onUpdate() {
    if (this.updateData.storeUrl) {
      window.open(this.updateData.storeUrl, '_system');
    }
    
    // Only close dialog if not mandatory
    if (!this.updateData.mandatory) {
      this.showUpdateDialog = false;
    }
  }
}